// Session token store. Access token held in-memory (never localStorage — XSS-exfil
// resistant, and it's short-lived + DPoP-bound anyway). We persist a lightweight
// "logged-in" hint + refresh token so a reload can attempt a silent renew.
//
// NOTE: because the DPoP private key is non-extractable and regenerated on reload,
// a persisted refresh token is only usable if the server rebinds the token to the
// NEW key on /auth/token/refresh (DPoP key-rotation on refresh, permitted by RFC 9449).
// If the server pins the original jkt hard, reload => re-login. See TODO in dpop.ts.

import type { AuthSuccess } from './types.js';
import { resetAutoSync } from '../lib/contact-sync.js';

const LS_REFRESH = 'vid.refresh';
const LS_HINT = 'vid.session';

let accessToken: string | null = null;
let refreshToken: string | null = null;
let dpopNonce: string | null = null; // last server-issued DPoP nonce

export const session = {
  get access(): string | null { return accessToken; },
  get refresh(): string | null { return refreshToken ?? localStorage.getItem(LS_REFRESH); },
  get nonce(): string | null { return dpopNonce; },
  get isLoggedIn(): boolean { return !!accessToken; },
  get hadSession(): boolean { return localStorage.getItem(LS_HINT) === '1'; },

  set(tokens: AuthSuccess | { access_token: string; refresh_token?: string }): void {
    accessToken = tokens.access_token;
    if (tokens.refresh_token) {
      refreshToken = tokens.refresh_token;
      localStorage.setItem(LS_REFRESH, tokens.refresh_token);
    }
    localStorage.setItem(LS_HINT, '1');
    // Fresh login re-enables the once-per-login auto Sync-all on My Contacts.
    try { resetAutoSync(sessionStorage); } catch { /* no sessionStorage */ }
  },

  setNonce(n: string | null): void { if (n) dpopNonce = n; },

  clear(): void {
    accessToken = null;
    refreshToken = null;
    dpopNonce = null;
    localStorage.removeItem(LS_REFRESH);
    localStorage.removeItem(LS_HINT);
    // Logout clears the auto-sync gate so the next login can auto-sync once.
    try { resetAutoSync(sessionStorage); } catch { /* no sessionStorage */ }
  },
};
